import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import AIGreeting from './components/AIGreeting';
import TenantPage from './Pages/TenantPage';
import LoginPage from './Pages/LoginPage';
import RegisterPage from './Pages/RegisterPage';
import ForgotPasswordPage from './Pages/ForgotPasswordPage';
import ResetPasswordPage from './Pages/ResetPasswordPage';
import DashboardPage from './Pages/DashboardPage';
import TransactionsPage from './Pages/TransactionsPage';
import InvoicesPage from './Pages/Invoices/InvoicesPage';
import CreateInvoicePage from './Pages/Invoices/CreateInvoicePage';
import InvoiceDetails from './Pages/Invoices/InvoiceDetails';
import CustomersPage from './Pages/CustomersPage';
import InventoryPage from './Pages/InventoryPage';
import ReportsPage from './Pages/ReportsPage';
import SettingsPage from './Pages/SettingsPage';
import SubscriptionPage from './Pages/SubscriptionPage';
import PaymentSuccessPage from './Pages/PaymentSuccessPage';
import OnboardingPage from './Pages/OnboardingPage';
import LandingPage from './Pages/LandingPage.jsx';
import PrivacyPolicy from './Pages/PrivacyPolicy';
import TermsOfService from './Pages/TermsOfService';
import Pricing from './Frontend/Pricing';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import TrialTimer from './components/TrialTimer';
import SubscriptionPopup from './components/SubscriptionPopup';
import AdminLogin from './Pages/AdminLogin';
import AdminDashboard from './Pages/AdminDashboard';
import AdminSetupPasskey from './Pages/AdminSetupPasskey';
import AdminCancelReview from './Pages/AdminCancelReview';
import { ensureValidTenantId } from './utils/auth';

// Bootstrap and UI styles
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets2/vendors/bootstrap-icons/font/bootstrap-icons.min.css';
import './assets2/css/style.css';
import './assets2/vendors/aos/aos.css';

// App specific styles
import './styles/global.css';
import './styles/app.css';
import './styles/sidebar.css';
import './styles/dashboard.css';
import './styles/invoices.css';
import './styles/customers.css';
import './styles/reports.css';
import './styles/settings.css';
import './styles/auth.css';
import './styles/subscription.css';
import './styles/payment.css';
import './styles/trial.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenantId');
  const [trialStatus, setTrialStatus] = useState({ loading: true, isExpired: false });

  // Only validate tenantId on protected routes
  ensureValidTenantId();

  // Check trial status
  useEffect(() => {
    const checkTrialStatus = async () => {
      if (!isAuthenticated || !token) return;

      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${apiUrl}/api/subscription/trial/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setTrialStatus({ loading: false, isExpired: data.isExpired });
        } else {
          setTrialStatus({ loading: false, isExpired: false });
        }
      } catch (error) {
        console.error('Error checking trial status:', error);
        setTrialStatus({ loading: false, isExpired: false });
      }
    };

    checkTrialStatus();
  }, [isAuthenticated, token]);

  if (!tenantId) {
    return <Navigate to="/" />;
  }

  if (!isAuthenticated || !token) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="app">
      <SubscriptionPopup />
      <TrialTimer />
      <Sidebar />
      <main className="main-content">
        {children}
      </main>
      <AIGreeting />
    </div>
  );
};

// Auth Layout Component
const AuthLayout = ({ children }) => {
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          {/* Admin Routes */}
          <Route path="/admin" element={
            <AuthLayout>
              <AdminLogin />
            </AuthLayout>
          } />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />

          {/* Public Routes with Auth Layout */}
          <Route path="/" element={
            <AuthLayout>
              <LandingPage />
            </AuthLayout>
          } />
          <Route path="/privacy" element={
            <AuthLayout>
              <PrivacyPolicy />
            </AuthLayout>
          } />
          <Route path="/terms" element={
            <AuthLayout>
              <TermsOfService />
            </AuthLayout>
          } />
          <Route path="/pricing" element={
            <AuthLayout>
              <Pricing />
            </AuthLayout>
          } />
          <Route path="/account" element={
            <AuthLayout>
              <TenantPage />
            </AuthLayout>
          } />
          <Route path="/login" element={
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          } />
          <Route path="/register" element={
            <AuthLayout>
              <RegisterPage />
            </AuthLayout>
          } />
          <Route path="/forgot-password" element={
            <AuthLayout>
              <ForgotPasswordPage />
            </AuthLayout>
          } />
          <Route path="/reset-password" element={
            <AuthLayout>
              <ResetPasswordPage />
            </AuthLayout>
          } />
          <Route path="/onboarding" element={
            <AuthLayout>
              <OnboardingPage />
            </AuthLayout>
          } />

          {/* Admin WebAuthn Routes */}
          <Route path="/admin/setup-biometrics" element={<AdminSetupPasskey />} />
          <Route path="/admin/cancel-review/:id" element={<AdminCancelReview />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <DashboardPage />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />
          <Route path="/transactions" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <TransactionsPage />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />

          {/* Invoice Routes */}
          <Route path="/invoices" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <InvoicesPage />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />
          <Route path="/invoices/create" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <CreateInvoicePage />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />
          <Route path="/invoices/:id" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <InvoiceDetails />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />
          <Route path="/invoices/:id/edit" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <CreateInvoicePage />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />

          <Route path="/customers" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <CustomersPage />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />
          <Route path="/inventory" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <InventoryPage />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <ReportsPage />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <SettingsPage />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />
          <Route path="/subscription" element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <SubscriptionPage />
              </SubscriptionProvider>
            </ProtectedRoute>
          } />
          <Route path="/payment-success" element={
            <ProtectedRoute>
              <PaymentSuccessPage />
            </ProtectedRoute>
          } />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;